import { LuRuler } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import { useConfirm } from '@contexts/Confirm.context';
import { useMapToolBusy } from '@hooks/useMapToolBusy';
import { MapControlButton } from '../../mapControls/StyledMapControls';

/**
 * Shown on every entry into measurement mode, to explain where the draw tools
 * are and that nothing drawn here is persisted. Deliberately not suppressed
 * after the first time: it warns about losing work, and that risk is new each
 * time the mode is entered. It auto-closes and never blocks the map.
 */
const INTRO_DIALOGUE = {
	header: 'Measurement Mode',
	body: (
		<>
			<p>Use the draw tools in the bottom toolbar.</p>

			<p>
				Note: measurements are temporary and will not be saved. Any sketches made here are
				lost when you leave measurement mode or refresh the page.
			</p>
		</>
	),
	fullWidth: false,
	autoClose: true,
	dismissible: true,
};

const DISCARD_CONFIRMATION = {
	title: 'Discard measurements?',
	message:
		'Leaving measurement mode removes the sketches you have drawn. They are not saved and cannot be restored.',
	confirmLabel: 'Discard',
	cancelLabel: 'Keep measuring',
};

/**
 * MeasurementToggle — enters and leaves measurement mode.
 *
 * Measurement is a mode, not a data layer: while it is on, `MeasurementLayer`
 * owns a temporary vector layer and `MeasurementTools` shows the draw tools in
 * the bottom toolbar. Both are driven by the single `measurementMode` flag this
 * button sets, so there is exactly one control and one source of truth.
 *
 * Leaving the mode destroys the sketches (they are never persisted), so the
 * user is asked to confirm whenever there is something to lose.
 */
const MeasurementToggle = () => {
	const { measurementMode, measurementLayer, dispatch } = useProject();
	const { confirm } = useConfirm();

	// Suspended while another tool owns the map: ending the mode mid-flow would
	// delete the layer and every sketch on it. Names itself as the owner so
	// measurement mode does not disable its own exit.
	const { busy: suspended, reason: blockedReason } = useMapToolBusy({ owner: 'measurement' });

	const handleToggle = async () => {
		if (!measurementMode) {
			dispatch({ type: 'setMeasurementMode', payload: true });
			dispatch({ type: 'setDialogue', payload: INTRO_DIALOGUE });
			return;
		}

		const hasSketches = measurementLayer?.getSource().getFeatures().length > 0;
		if (hasSketches && !(await confirm(DISCARD_CONFIRMATION))) return;

		dispatch({ type: 'setMeasurementMode', payload: false });
	};

	// Doubles as the accessible name rather than living only in `title`: browsers
	// suppress title tooltips on disabled buttons, so a static name would leave
	// keyboard and screen-reader users no way to learn why the control is off.
	const label =
		blockedReason || (measurementMode ? 'Exit measurement mode' : 'Measure distance and area');

	return (
		<MapControlButton
			type="button"
			onClick={handleToggle}
			disabled={suspended}
			$active={measurementMode}
			aria-pressed={measurementMode}
			aria-label={label}
			title={label}>
			<LuRuler />
		</MapControlButton>
	);
};

export default MeasurementToggle;
